import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { useCxRequestMutation } from '@/__generated/es-api'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { useAppDispatch } from '@/app/store/redux-hooks'

type RequestData = {
  message: string
  country?: string | null
}

// Hook for sending operational / provisioning requests from dashboard request form.
export const useSendRequest = () => {
  const orgId = useSelector(selectOrgId)

  const dispatch = useAppDispatch()
  const [sendRequestMutation, mutationResult] = useCxRequestMutation()

  const sendRequest = useCallback(
    async (data: RequestData) => {
      try {
        await sendRequestMutation({
          orgId: orgId.toString(),
          body: {
            message: data.message,
            country: data.country ?? undefined,
          },
        }).unwrap()

        dispatch(
          createNotification({
            'request-success': {
              type: 'success',
              message: "Request received — our team is on it and we'll follow up shortly.",
            },
          }),
        )
        return true
      } catch {
        dispatch(
          createNotification({
            'request-error': {
              type: 'error',
              message:
                "We couldn't send your request. Please try again or reach out via live chat in the lower-right corner.",
            },
          }),
        )
        return false
      }
    },
    [orgId, dispatch, sendRequestMutation],
  )

  return [sendRequest, mutationResult] as const
}

export type UseSendRequestReturn = ReturnType<typeof useSendRequest>
